import { listOf } from '../../lib/format';
import { Button } from '../ui/Button';

/**
 * The foot of a form that creates a job, or gives jobs their type: the button that sends it
 * ("Create job", "Set service type", "Save"), an optional way out, and, while the button cannot
 * be pressed, the reason. The new-job sheet and every service type step (`ServiceTypeStep`: a
 * conversion's, the Jobs list's "Set service type", the job page's change) draw it, so all say
 * what they are waiting for the same way.
 *
 * A button that is simply grey leaves the person to guess which field it is waiting for, so
 * "Still needed: …" names each one, in the order the form asks, and is the button's description
 * (`aria-describedby`): a screen reader that lands on the button hears why it is off.
 *
 * `aria-disabled` and a guard in each handler, never `disabled`: a button that turns `disabled`
 * while it has focus drops focus to <body> (LESSONS 2026-09-28), and pressing the button does
 * exactly that as the write starts. An `aria-disabled` button also stays in the tab order, so a
 * keyboard or screen-reader user can land on it while it is off and hear why.
 *
 * No hooks (`reasonId` comes from the caller's `useId`), so a test can call it as the function it
 * is and press the real buttons it returns (this package has no DOM).
 */
export function CreateFooter({
  label,
  pendingLabel = 'Creating…',
  missing,
  pending,
  locked = false,
  reasonId,
  onSubmit,
  cancel,
}: {
  /** What the button says when it can be pressed ("Create job", "Set service type"). */
  label: string;
  /** What it says while this form's write is on its way. A set is not a create ("Setting…"). */
  pendingLabel?: string;
  /** What the form still needs, in the order it asks for it. Empty means ready. */
  missing: readonly string[];
  /**
   * This form's own write is on its way: the button says `pendingLabel`, and neither button
   * does anything. The way out waits too: the write is already sent, and a Cancel that let it
   * land would say nothing happened when something did.
   */
  pending: boolean;
  /**
   * ANOTHER write on the same record is on its way (the quote page's "Create invoice", say):
   * the button does nothing until it lands, and keeps its label, since nothing is being sent.
   * The server decides what the second write finds; this keeps one tab from sending both.
   */
  locked?: boolean;
  /** The id the "Still needed" line takes, and the button points at. */
  reasonId: string;
  /** The press, once nothing is missing and nothing is on its way. */
  onSubmit: () => void;
  /**
   * A way out that sends nothing ("Cancel", "Back", "Clear selection"). `off` while there is
   * nothing to leave or clear (the Jobs list with nothing ticked): `aria-disabled`, like the
   * button beside it, and the press does nothing.
   */
  cancel?: { label: string; onCancel: () => void; off?: boolean };
}) {
  const blocked = missing.length > 0;
  const off = blocked || pending || locked;
  const cancelOff = pending || (cancel?.off ?? false);
  return (
    <div className="space-y-2">
      {blocked ? (
        <p id={reasonId} className="text-xs text-muted-foreground">
          Still needed: {listOf(missing)}.
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          aria-disabled={off}
          aria-describedby={blocked ? reasonId : undefined}
          onClick={() => {
            if (!off) onSubmit();
          }}
        >
          {pending ? pendingLabel : label}
        </Button>
        {cancel ? (
          <Button
            variant="ghost"
            aria-disabled={cancelOff}
            onClick={() => {
              if (!cancelOff) cancel.onCancel();
            }}
          >
            {cancel.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
